import type { EstimateDetail, Inspection, TemplateVersion } from 'used-vehicle-contracts';
import {
  buildValuationInputs,
  type BuildInputsResult,
  type CompiledPolicy,
  compilePolicy,
  type EquationDef,
  quickRange as computeQuickRange,
  type QuickRangeResult,
  type RunResult,
  runForOffer,
  type ValuationBundle,
  validateOutputs,
  type ValidateOutputsResult,
} from 'used-vehicle-engine';

/**
 * The estimator's **local** valuation (plan §2.5).
 *
 * The browser runs the same `used-vehicle-engine` the service runs, over the same bundle the server
 * builds, so the appraisal and the quick range move with the keystroke rather than with the round trip.
 * The server's answer replaces this one when the debounced save returns; if the two ever disagreed the
 * bug would be in one of these two mappings, which is why they are deliberately written to mirror each
 * other field for field (`domain/valuation-bundle.ts` is the other half).
 *
 * The rules are the server's, repeated here because the engine must not depend on either side:
 * `daysInStock = 0`, `isSold = 0`, `acquisitionBasis = ctaOffer ?? 0` (with `runForOffer` filling in the
 * ceiling), `reconditioningResolved = 0` while cost items are a later phase, and trade-in never priced
 * from an estimate.
 */
export interface LocalValuation {
  built: BuildInputsResult;
  run: RunResult;
  validation: ValidateOutputsResult;
  bundle: ValuationBundle;
  ok: boolean;
}

/** The form's own copy of the facts, so a keystroke prices before it is saved. */
export interface EstimateDraft {
  modelYear: number | null;
  referencePrice: number | null;
  odometerKm: number | null;
  ownerCount: number | null;
  fuel: EstimateDetail['fuel'];
  registrationExpiry: string | null;
  faceliftApplies: boolean | null;
  customsApplies: boolean | null;
  hasSpareKey: boolean;
  hasOwnersManual: boolean;
  hasToolKit: boolean;
  hasSpareTire: boolean;
  acquisitionType: EstimateDetail['acquisitionType'];
  profitMode: EstimateDetail['profitMode'];
  taxTrafficEstimate: number | null;
  otherDirectCostEstimate: number | null;
  /** Typed registration and transfer fees; `null` = the template prices them (plan §11.19). */
  registrationTransferFee: number | null;
  ctaOffer: number | null;
  valuationDate: string | null;
}

export interface InspectionDraft {
  grade7: Inspection['grade7'];
  interior: Inspection['interior'];
  engine: Inspection['engine'];
  hybrid: Inspection['hybrid'];
  tires: Inspection['tires'];
  damageGroup1Index: number | null;
  damageGroup2Index: number | null;
}

export const UNRECORDED_INSPECTION: InspectionDraft = {
  grade7: 'UNKNOWN',
  interior: 'UNKNOWN',
  engine: 'UNKNOWN',
  hybrid: 'UNKNOWN',
  tires: 'UNKNOWN',
  damageGroup1Index: null,
  damageGroup2Index: null,
};

export const draftFromDetail = (detail: EstimateDetail): EstimateDraft => ({
  modelYear: detail.modelYear,
  referencePrice: detail.referencePrice,
  odometerKm: detail.odometerKm,
  ownerCount: detail.ownerCount,
  fuel: detail.fuel,
  registrationExpiry: detail.registrationExpiry,
  faceliftApplies: detail.faceliftApplies,
  customsApplies: detail.customsApplies,
  hasSpareKey: detail.hasSpareKey,
  hasOwnersManual: detail.hasOwnersManual,
  hasToolKit: detail.hasToolKit,
  hasSpareTire: detail.hasSpareTire,
  acquisitionType: detail.acquisitionType,
  profitMode: detail.profitMode,
  taxTrafficEstimate: detail.taxTrafficEstimate,
  otherDirectCostEstimate: detail.otherDirectCostEstimate,
  registrationTransferFee: detail.registrationTransferFee,
  ctaOffer: detail.ctaOffer,
  valuationDate: detail.valuationDate,
});

export const inspectionDraftFrom = (inspection: Inspection | null | undefined): InspectionDraft =>
  inspection
    ? {
        grade7: inspection.grade7,
        interior: inspection.interior,
        engine: inspection.engine,
        hybrid: inspection.hybrid,
        tires: inspection.tires,
        damageGroup1Index: inspection.damageGroup1Index,
        damageGroup2Index: inspection.damageGroup2Index,
      }
    : { ...UNRECORDED_INSPECTION };

/** The overrides map flattened to the `{ key: value }` the engine reads. */
export const overrideValues = (overrides: EstimateDetail['paramOverrides']): Record<string, number> =>
  Object.fromEntries(
    Object.entries(overrides ?? {})
      .filter(([, override]) => override && Number.isFinite(override.value))
      .map(([key, override]) => [key, override.value]),
  );

export interface BundleInput {
  facts: EstimateDraft;
  inspection: InspectionDraft;
  overrides: EstimateDetail['paramOverrides'];
  templateVersion: Pick<TemplateVersion, 'params' | 'damageGroup1' | 'damageGroup2' | 'overridableKeys'> | null;
  /** `YYYY-MM-DD`; used only when the estimate carries no valuation date of its own. */
  today: string;
}

export const toEstimateBundle = ({ facts, inspection, overrides, templateVersion, today }: BundleInput): ValuationBundle => ({
  owner: 'ESTIMATE',
  valuationDate: facts.valuationDate ?? today,
  facts: {
    modelYear: facts.modelYear,
    referencePrice: facts.referencePrice,
    odometerKm: facts.odometerKm,
    ownerCount: facts.ownerCount,
    fuel: facts.fuel,
    registrationExpiry: facts.registrationExpiry,
    faceliftApplies: facts.faceliftApplies,
    customsApplies: facts.customsApplies,
    hasSpareKey: facts.hasSpareKey,
    hasOwnersManual: facts.hasOwnersManual,
    hasToolKit: facts.hasToolKit,
    hasSpareTire: facts.hasSpareTire,
    acquisitionType: facts.acquisitionType,
    profitMode: facts.profitMode,
    taxTrafficEstimate: facts.taxTrafficEstimate,
    otherDirectCostEstimate: facts.otherDirectCostEstimate,
    registrationTransferFee: facts.registrationTransferFee,
    ctaOffer: facts.ctaOffer,
  },
  inspection: { ...inspection },
  template: {
    params: templateVersion?.params ?? {},
    damageGroup1: templateVersion?.damageGroup1 ?? [],
    damageGroup2: templateVersion?.damageGroup2 ?? [],
    ...(templateVersion ? { overridableKeys: templateVersion.overridableKeys } : {}),
  },
  overrides: overrideValues(overrides),
  costItems: [],
  tradeIn: null,
});

export const compileEquations = (equations: readonly EquationDef[]): CompiledPolicy => compilePolicy(equations).policy;

export const evaluateLocally = (policy: CompiledPolicy, bundle: ValuationBundle): LocalValuation => {
  const built = buildValuationInputs(bundle);
  const run = runForOffer(policy, built.inputs, { tradeIn: built.tradeIn, costResolutions: built.costResolutions });
  const validation = validateOutputs(run);

  return { built, run, validation, bundle, ok: run.errors.length === 0 && validation.issues.length === 0 };
};

/**
 * The quick range's one deviation from the appraisal (plan §5.11): a facelift or customs question
 * nobody has answered is read as "does not apply" **and the assumption is labelled**, so four
 * indicative prices appear the moment an odometer reading is typed. The appraisal above still treats
 * both as missing, which is what keeps the estimate a draft until somebody answers.
 */
export type QuickRangeAssumption = 'FACELIFT' | 'CUSTOMS';

export interface LocalQuickRange extends QuickRangeResult {
  assumptions: QuickRangeAssumption[];
}

export const evaluateQuickRange = (policy: CompiledPolicy, bundle: ValuationBundle): LocalQuickRange => {
  const assumptions: QuickRangeAssumption[] = [];

  if (bundle.facts.faceliftApplies === null || bundle.facts.faceliftApplies === undefined) {
    assumptions.push('FACELIFT');
  }

  if (bundle.facts.customsApplies === null || bundle.facts.customsApplies === undefined) {
    assumptions.push('CUSTOMS');
  }

  const result = computeQuickRange({
    policy,
    bundle: {
      ...bundle,
      facts: { ...bundle.facts, faceliftApplies: bundle.facts.faceliftApplies ?? false, customsApplies: bundle.facts.customsApplies ?? false },
    },
  });

  return { ...result, assumptions };
};

/** The zone a workspace is priced in when the session somehow has no user to ask (plan §11 fix 13). */
export const DEFAULT_TIME_ZONE = 'Asia/Amman';

/**
 * `YYYY-MM-DD` in the **tenant's** zone, which is the calendar day an age and a registration countdown
 * are measured against (plan §11 fix 13). Amman runs three hours ahead of UTC, so a browser asking for
 * the UTC day before 03:00 would price a vehicle as a day younger than the service does. `en-CA` is the
 * locale whose short date already *is* `YYYY-MM-DD`; a zone the browser will not accept falls back to
 * UTC rather than failing a keystroke.
 */
export const todayIso = (timeZone: string): string => {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
};
